/**
 * usePushNotifications · custom hook per Web Push via VAPID.
 * Funziona su Chrome/Firefox/Edge/iOS Safari 16.4+ in modalità standalone.
 *
 * Espone: { isSupported, status, subscribe, unsubscribe, sendTest, loading, error }
 *  - status: "default" | "granted" | "denied" | "subscribed"
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const out = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) out[i] = rawData.charCodeAt(i);
  return out;
}

export default function usePushNotifications() {
  const [isSupported, setSupported] = useState(false);
  const [status, setStatus]   = useState("default");
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    const ok =
      typeof window !== "undefined" &&
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window;
    setSupported(ok);
    if (!ok) return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) setStatus("subscribed");
        else setStatus(Notification.permission || "default");
      } catch {
        setStatus(Notification.permission || "default");
      }
    })();
  }, []);

  const subscribe = useCallback(async () => {
    if (!isSupported) {
      setError("Notifiche non supportate da questo browser");
      return false;
    }
    setLoading(true); setError(null);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setStatus(perm);
        setError(perm === "denied"
          ? "Hai negato le notifiche. Riattivale dalle impostazioni del browser."
          : "Notifiche non attivate.");
        return false;
      }
      const reg = await navigator.serviceWorker.ready;
      // already subscribed?
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        const { data } = await api.get("/push/public-key");
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.publicKey),
        });
      }
      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys,
        user_agent: navigator.userAgent,
      });
      setStatus("subscribed");
      return true;
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Errore attivazione notifiche");
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported]);

  const unsubscribe = useCallback(async () => {
    if (!isSupported) return;
    setLoading(true); setError(null);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        try { await api.post("/push/unsubscribe", { endpoint: sub.endpoint }); }
        catch { /* ignore */ }
        await sub.unsubscribe();
      }
      setStatus("default");
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported]);

  const sendTest = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/push/test");
      return data;
    } finally {
      setLoading(false);
    }
  }, []);

  return { isSupported, status, loading, error, subscribe, unsubscribe, sendTest };
}
